// src/Components/FeedBack.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import './Feedback.css';

const FeedBack = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    averageOverall: 0,
    averageProfessionalism: 0,
    averageRelevance: 0,
    averageDifficulty: 0,
    averagePlatform: 0,
    recommendationBreakdown: { Yes: 0, No: 0, Maybe: 0 }
  });

  useEffect(() => {
    loadFeedbacks();
    loadFeedbackStats();
  }, []);

  const loadFeedbacks = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('authToken');
      
      // Get company-specific feedbacks
      const response = await axios.get(`${API_BASE_URL}/api/applications/admin/feedbacks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setFeedbacks(response.data.data);
      }
    } catch (error) {
      console.error('Error loading feedbacks:', error);
      if (error.response?.status === 401) {
        alert('Please login again to view feedbacks');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadFeedbackStats = async () => {
    try {
      const token = sessionStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/api/applications/admin/feedback-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error loading feedback stats:', error);
    }
  };

  // Filter feedbacks
  const filteredFeedbacks = feedbacks.filter(feedback => {
    const matchesFilter = filter === 'all' || 
      (filter === 'high_rating' && feedback.feedback?.overallExperience >= 4) ||
      (filter === 'medium_rating' && feedback.feedback?.overallExperience === 3) ||
      (filter === 'low_rating' && feedback.feedback?.overallExperience <= 2) ||
      (filter === 'recommend' && feedback.feedback?.wouldRecommend === 'Yes') ||
      (filter === 'not_recommend' && feedback.feedback?.wouldRecommend === 'No');
    
    const matchesSearch = searchTerm === '' || 
      feedback.candidateName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      feedback.candidateEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      feedback.position?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      feedback.companyName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  const getRatingStars = (rating) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  const getRecommendationBadge = (recommend) => {
    switch(recommend) {
      case 'Yes': return <span className="recommend-badge recommend-yes">👍 Would Recommend</span>;
      case 'No': return <span className="recommend-badge recommend-no">👎 Would Not Recommend</span>;
      case 'Maybe': return <span className="recommend-badge recommend-maybe">🤔 Maybe</span>;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="feedback-loading">
        <div className="loading-spinner"></div>
        <p>Loading feedback...</p>
      </div>
    );
  }

  return (
    <div className="feedback-container">
      {/* Header Section */}
      <div className="feedback-header">
        <div className="feedback-title-section">
          <h2 className="feedback-title">Interview Feedback</h2>
          <p className="feedback-subtitle">
            Candidate feedback collected after interviews for your company
          </p>
        </div>
        <div className="feedback-stats">
          <div className="stat-badge">
            <span className="stat-number">{feedbacks.length}</span>
            <span className="stat-label">Total Responses</span>
          </div>
          <div className="stat-badge">
            <span className="stat-number">{stats.averageOverall || 0}</span>
            <span className="stat-label">Avg Rating</span>
          </div>
          <div className="stat-badge">
            <span className="stat-number">{stats.recommendationBreakdown?.Yes || 0}</span>
            <span className="stat-label">Would Recommend</span>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="feedback-stats-grid">
        <div className="stat-card">
          <div className="stat-card-icon">📊</div>
          <div className="stat-card-content">
            <span className="stat-card-value">{stats.averageOverall || 0} / 5</span>
            <span className="stat-card-label">Overall Experience</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon">👔</div>
          <div className="stat-card-content">
            <span className="stat-card-value">{stats.averageProfessionalism || 0} / 5</span>
            <span className="stat-card-label">Interviewer Professionalism</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon">📝</div>
          <div className="stat-card-content">
            <span className="stat-card-value">{stats.averageRelevance || 0} / 5</span>
            <span className="stat-card-label">Question Relevance</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon">⚙️</div>
          <div className="stat-card-content">
            <span className="stat-card-value">{stats.averageDifficulty || 0} / 5</span>
            <span className="stat-card-label">Technical Difficulty</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon">💻</div>
          <div className="stat-card-content">
            <span className="stat-card-value">{stats.averagePlatform || 0} / 5</span>
            <span className="stat-card-label">Platform Experience</span>
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="feedback-filters">
        <div className="search-box">
          <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search by name, email, position, company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-tabs">
          <button 
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button 
            className={`filter-tab ${filter === 'high_rating' ? 'active' : ''}`}
            onClick={() => setFilter('high_rating')}
          >
            High Rating (4-5)
          </button>
          <button 
            className={`filter-tab ${filter === 'recommend' ? 'active' : ''}`}
            onClick={() => setFilter('recommend')}
          >
            Would Recommend
          </button>
          <button 
            className={`filter-tab ${filter === 'not_recommend' ? 'active' : ''}`}
            onClick={() => setFilter('not_recommend')}
          >
            Would Not Recommend
          </button>
        </div>

        <button className="refresh-btn" onClick={() => { loadFeedbacks(); loadFeedbackStats(); }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {filteredFeedbacks.length === 0 ? (
        <div className="feedback-empty">
          <svg className="empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
          <h3>No Feedback Available</h3>
          <p>Interview feedback from candidates who applied to your jobs will appear here.</p>
        </div>
      ) : (
        <div className="feedback-list">
          {filteredFeedbacks.map((feedback) => (
            <div key={feedback._id} className="feedback-card">
              <div className="feedback-card-header">
                <div className="feedback-candidate-info">
                  <div className="candidate-avatar">
                    {feedback.candidateName?.charAt(0) || 'C'}
                  </div>
                  <div className="candidate-details">
                    <h4 className="candidate-name">{feedback.candidateName || 'Candidate'}</h4>
                    <p className="candidate-position">{feedback.position || 'Position'}</p>
                    <p className="candidate-company">{feedback.companyName || 'Company'}</p>
                    <p className="candidate-email">{feedback.candidateEmail}</p>
                  </div>
                </div>
                <div className="overall-rating">
                  {getRatingStars(feedback.feedback?.overallExperience || 0)}
                </div>
              </div>

              <div className="feedback-card-body">
                {/* Ratings Grid */}
                <div className="ratings-grid">
                  <div className="rating-item">
                    <span className="rating-label">Interviewer Professionalism:</span>
                    <span className="rating-stars">{getRatingStars(feedback.feedback?.interviewerProfessionalism || 0)}</span>
                  </div>
                  <div className="rating-item">
                    <span className="rating-label">Question Relevance:</span>
                    <span className="rating-stars">{getRatingStars(feedback.feedback?.questionsRelevance || 0)}</span>
                  </div>
                  <div className="rating-item">
                    <span className="rating-label">Technical Difficulty:</span>
                    <span className="rating-stars">{getRatingStars(feedback.feedback?.technicalDifficulty || 0)}</span>
                  </div>
                  <div className="rating-item">
                    <span className="rating-label">Platform Experience:</span>
                    <span className="rating-stars">{getRatingStars(feedback.feedback?.platformExperience || 0)}</span>
                  </div>
                </div>

                {/* Would Recommend */}
                <div className="recommendation-section">
                  {getRecommendationBadge(feedback.feedback?.wouldRecommend)}
                </div>

                {/* Feedback Comments */}
                {feedback.feedback?.likedMost && (
                  <div className="feedback-section">
                    <label className="feedback-label">👍 What they liked most:</label>
                    <p className="feedback-text">{feedback.feedback.likedMost}</p>
                  </div>
                )}

                {feedback.feedback?.improvements && (
                  <div className="feedback-section">
                    <label className="feedback-label">💡 Suggestions for improvement:</label>
                    <p className="feedback-text">{feedback.feedback.improvements}</p>
                  </div>
                )}

                {feedback.feedback?.comments && (
                  <div className="feedback-section">
                    <label className="feedback-label">💬 Additional Comments:</label>
                    <p className="feedback-text">{feedback.feedback.comments}</p>
                  </div>
                )}
              </div>

              <div className="feedback-card-footer">
                <div className="feedback-meta">
                  <span className="feedback-date">
                    Submitted: {new Date(feedback.submittedAt).toLocaleDateString()}
                  </span>
                  <span className="feedback-time">
                    {new Date(feedback.submittedAt).toLocaleTimeString()}
                  </span>
                </div>
                <div className="feedback-actions">
                  <button 
                    className="view-details-btn"
                    onClick={() => setSelectedFeedback(feedback)}
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal for detailed view - Fixed White Background & Responsive */}
      {selectedFeedback && (
        <div className="feedback-modal-overlay" onClick={() => setSelectedFeedback(null)}>
          <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Feedback Details</h3>
              <button className="modal-close" onClick={() => setSelectedFeedback(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <label>Candidate Name:</label>
                <p>{selectedFeedback.candidateName || 'N/A'}</p>
              </div>
              <div className="detail-section">
                <label>Email:</label>
                <p>{selectedFeedback.candidateEmail || 'N/A'}</p>
              </div>
              <div className="detail-section">
                <label>Position Applied:</label>
                <p>{selectedFeedback.position || 'N/A'}</p>
              </div>
              <div className="detail-section">
                <label>Company:</label>
                <p>{selectedFeedback.companyName || 'N/A'}</p>
              </div>
              
              <div className="detail-section">
                <label>Ratings:</label>
                <div className="ratings-detail">
                  <div className="rating-row">
                    <span className="rating-label-detail">Overall Experience:</span>
                    <span className="rating-value">{getRatingStars(selectedFeedback.feedback?.overallExperience || 0)}</span>
                  </div>
                  <div className="rating-row">
                    <span className="rating-label-detail">Interviewer Professionalism:</span>
                    <span className="rating-value">{getRatingStars(selectedFeedback.feedback?.interviewerProfessionalism || 0)}</span>
                  </div>
                  <div className="rating-row">
                    <span className="rating-label-detail">Question Relevance:</span>
                    <span className="rating-value">{getRatingStars(selectedFeedback.feedback?.questionsRelevance || 0)}</span>
                  </div>
                  <div className="rating-row">
                    <span className="rating-label-detail">Technical Difficulty:</span>
                    <span className="rating-value">{getRatingStars(selectedFeedback.feedback?.technicalDifficulty || 0)}</span>
                  </div>
                  <div className="rating-row">
                    <span className="rating-label-detail">Platform Experience:</span>
                    <span className="rating-value">{getRatingStars(selectedFeedback.feedback?.platformExperience || 0)}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <label>Would Recommend:</label>
                <p className={`recommend-text recommend-${selectedFeedback.feedback?.wouldRecommend?.toLowerCase()}`}>
                  {selectedFeedback.feedback?.wouldRecommend || 'N/A'}
                </p>
              </div>

              {selectedFeedback.feedback?.likedMost && (
                <div className="detail-section">
                  <label>What they liked most:</label>
                  <div className="message-box">{selectedFeedback.feedback.likedMost}</div>
                </div>
              )}

              {selectedFeedback.feedback?.improvements && (
                <div className="detail-section">
                  <label>Suggestions for improvement:</label>
                  <div className="message-box">{selectedFeedback.feedback.improvements}</div>
                </div>
              )}

              {selectedFeedback.feedback?.comments && (
                <div className="detail-section">
                  <label>Additional Comments:</label>
                  <div className="message-box">{selectedFeedback.feedback.comments}</div>
                </div>
              )}

              <div className="detail-section">
                <label>Submitted On:</label>
                <p>{new Date(selectedFeedback.submittedAt).toLocaleString()}</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="close-modal-btn" onClick={() => setSelectedFeedback(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedBack;